/**
 * The playlist editor's arithmetic and list surgery.
 *
 * All of it is pure and free of React, for two reasons. The first is that the
 * total run time is the number a store owner reasons about — "the loop is four
 * minutes, so a customer waiting for a pizza sees it twice" — and a number that
 * matters that much deserves a test rather than an expression buried in JSX. The
 * second is reordering: it has to work from a mouse and from a keyboard, and both
 * paths call the same function here, so the two can never drift apart.
 *
 * Every function returns the array it was given, unchanged and identical, when a
 * move would do nothing. React leans on that identity, and so does the "unsaved
 * changes" check.
 */

import {
  ACCEPTED_VIDEO_MIME,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_FIT,
  DEFAULT_IMAGE_DURATION_SEC,
  DEFAULT_VIDEO_REPEAT_COUNT,
} from '@pbtv/shared';
import type { FitMode, PlaylistItem } from '@pbtv/shared';
import type { MediaAsset } from '../../lib/media.js';
import { FIT_MODES, fitHint, fitLabel } from '../preview/geometry.js';

export function isVideoMediaType(type: string): boolean {
  return (ACCEPTED_VIDEO_MIME as readonly string[]).includes(type);
}

/**
 * One row of the editor. Playlist items carry no identity of their own — the same
 * asset may legitimately appear three times in one loop with different durations —
 * so the editor gives each row a local id, purely so React can follow a row
 * across a reorder and keep focus on the button that moved it. The id is never
 * saved and never leaves the browser.
 */
export interface DraftItem {
  uid: string;
  item: PlaylistItem;
}

let uidCounter = 0;

export function nextItemUid(): string {
  uidCounter += 1;
  return `row_${uidCounter}`;
}

export function toDraftItems(items: readonly PlaylistItem[]): DraftItem[] {
  return items.map((item) => ({ uid: nextItemUid(), item }));
}

export function toPlaylistItems(draft: readonly DraftItem[]): PlaylistItem[] {
  return draft.map((row) => row.item);
}

/**
 * A newly added row. The defaults are written out rather than left null, so the
 * editor's fields show the number that will actually be used and the saved
 * document says what the manager chose. `durationSec` on a video and
 * `repeatCount` on a still are the two fields the manifest ignores, and they stay
 * null so nothing implies otherwise.
 */
export function draftItemFor(asset: Pick<MediaAsset, 'assetId' | 'type'>): DraftItem {
  const video = isVideoMediaType(asset.type);
  return {
    uid: nextItemUid(),
    item: {
      assetId: asset.assetId,
      durationSec: video ? null : DEFAULT_IMAGE_DURATION_SEC,
      repeatCount: video ? DEFAULT_VIDEO_REPEAT_COUNT : null,
      muted: true,
      fit: DEFAULT_FIT,
      backgroundColor: DEFAULT_BACKGROUND_COLOR,
    },
  };
}

/** Moves one entry, clamping the destination into the list. */
export function moveItem<T>(items: readonly T[], from: number, to: number): readonly T[] {
  if (from < 0 || from >= items.length) return items;
  const target = Math.min(Math.max(to, 0), items.length - 1);
  if (target === from) return items;

  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return items;
  next.splice(target, 0, moved);
  return next;
}

export function removeAt<T>(items: readonly T[], index: number): readonly T[] {
  if (index < 0 || index >= items.length) return items;
  return items.filter((_entry, position) => position !== index);
}

export function patchItemAt(
  items: readonly DraftItem[],
  index: number,
  patch: Partial<PlaylistItem>,
): readonly DraftItem[] {
  const row = items[index];
  if (row === undefined) return items;
  return items.map((entry, position) =>
    position === index ? { uid: entry.uid, item: { ...entry.item, ...patch } } : entry,
  );
}

export type AssetLookup = (assetId: string) => MediaAsset | undefined;

/**
 * How long this item holds the screen, in milliseconds, or null when that cannot
 * be known: an asset the library did not hand back, or a video whose length was
 * never probed. Nulls are counted separately rather than treated as zero — a
 * total that quietly omits a two-minute video is worse than a total that says it
 * is incomplete.
 */
export function itemRuntimeMs(item: PlaylistItem, asset: MediaAsset | undefined): number | null {
  if (asset === undefined) return null;

  if (!isVideoMediaType(asset.type)) {
    return Math.round((item.durationSec ?? DEFAULT_IMAGE_DURATION_SEC) * 1000);
  }

  const natural = asset.durationMs;
  if (natural === null || !Number.isFinite(natural) || natural <= 0) return null;
  return Math.round(natural * (item.repeatCount ?? DEFAULT_VIDEO_REPEAT_COUNT));
}

export interface PlaylistRuntime {
  /** Total of everything whose length is known. */
  totalMs: number;
  /** Videos in the list whose natural length was never recorded. */
  unknownCount: number;
  /** Items whose asset is not in the library the editor can see. */
  missingCount: number;
}

export function playlistRuntime(
  items: readonly PlaylistItem[],
  lookup: AssetLookup,
): PlaylistRuntime {
  let totalMs = 0;
  let unknownCount = 0;
  let missingCount = 0;

  for (const item of items) {
    const asset = lookup(item.assetId);
    if (asset === undefined) {
      missingCount += 1;
      continue;
    }
    const runtime = itemRuntimeMs(item, asset);
    if (runtime === null) {
      unknownCount += 1;
      continue;
    }
    totalMs += runtime;
  }

  return { totalMs, unknownCount, missingCount };
}

/**
 * A duration in the words a person would use out loud: "48s", "3m 20s", "1h 4m".
 * Seconds are dropped once there are hours, because at that length they are noise.
 */
export function formatRuntime(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
  if (minutes > 0) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** The per-row reading of the same number, plus what produced it. */
export function describeItemLength(item: PlaylistItem, asset: MediaAsset | undefined): string {
  if (asset === undefined) return 'Asset unavailable';

  const runtime = itemRuntimeMs(item, asset);
  if (!isVideoMediaType(asset.type)) return runtime === null ? 'Unknown' : formatRuntime(runtime);

  const repeats = item.repeatCount ?? DEFAULT_VIDEO_REPEAT_COUNT;
  if (runtime === null) return repeats === 1 ? 'Length unknown' : `${repeats}x, length unknown`;
  return repeats === 1 ? formatRuntime(runtime) : `${repeats}x · ${formatRuntime(runtime)}`;
}

export interface FitChoice {
  value: FitMode;
  label: string;
  hint: string;
}

export const FIT_CHOICES: readonly FitChoice[] = FIT_MODES.map((value) => ({
  value,
  label: fitLabel(value),
  hint: fitHint(value),
}));

/**
 * Whether two item lists would produce the same document. Field-by-field rather
 * than a JSON comparison, because key order in an object literal is not part of
 * what a playlist means and must not be able to light up the unsaved-changes
 * warning.
 */
export function itemsEqual(left: readonly PlaylistItem[], right: readonly PlaylistItem[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((item, index) => {
    const other = right[index];
    if (other === undefined) return false;
    return (
      item.assetId === other.assetId &&
      item.durationSec === other.durationSec &&
      item.repeatCount === other.repeatCount &&
      item.muted === other.muted &&
      item.fit === other.fit &&
      item.backgroundColor === other.backgroundColor
    );
  });
}
